import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { fetchCategoriesWithSubCategories } from '../features/categories/categoriesSlice';
import { fetchProducts } from '../features/products/productsSlice';
import {
  addToCart,
  addToCartAsync,
  fetchCart,
  removeFromCart,
  removeFromCartAsync,
  updateCartItemAsync,
  updateQuantity,
} from '../features/cart/cartSlice';
import Header from './Header';
import Footer from './Footer';
import { API_URL, BROKEN_IMAGE_PLACEHOLDER } from '../config';
import { formatPrice, getImageUrl, getListedProductStock, getListedProductVariants, getProductMrp, getProp } from '../utils/productHelpers';
import './CategoryProductListPage.css';

const FILTER_PREVIEW_COUNT = 3;
const SORT_OPTIONS = [
  { value: 'recommended', label: 'Recommended' },
  { value: 'price-asc', label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'name-asc', label: 'Name: A to Z' },
];

function FilterSection({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="plp-filter-block">
      <button type="button" className="plp-filter-toggle" onClick={() => setOpen((v) => !v)}>
        <span>{title}</span>
        <span className="plp-filter-chevron" aria-hidden="true">{open ? '▼' : '▶'}</span>
      </button>
      {open ? <div className="plp-filter-body">{children}</div> : null}
    </div>
  );
}

const CategoryProductListPage = () => {
  const { categoryId: categoryIdParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const categoryId = parseInt(categoryIdParam, 10);
  const subCategoryId = searchParams.get('subcategory')
    ? parseInt(searchParams.get('subcategory'), 10)
    : null;
  const tertiaryCategoryId = searchParams.get('tertiary')
    ? parseInt(searchParams.get('tertiary'), 10)
    : null;
  const quaternaryCategoryId = searchParams.get('quaternary')
    ? parseInt(searchParams.get('quaternary'), 10)
    : null;

  const categories = useSelector((state) => state.categories.itemsWithSubCategories);
  const categoriesStatus = useSelector((state) => state.categories.subCategoriesStatus);
  const products = useSelector((state) => state.products.items);
  const productsStatus = useSelector((state) => state.products.status);
  const productsError = useSelector((state) => state.products.error);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const userRole = useSelector((state) => state.auth.userRole);
  const cartItems = useSelector((state) => state.cart.items);

  const [selectedBrands, setSelectedBrands] = useState([]);
  const [priceMin, setPriceMin] = useState(null);
  const [priceMax, setPriceMax] = useState(null);
  const [sortBy, setSortBy] = useState('recommended');
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [showAllSubCategories, setShowAllSubCategories] = useState(false);
  const [categoryThumbUrl, setCategoryThumbUrl] = useState(null);

  const isSeller = isLoggedIn && userRole === 'Seller';
  const isAdmin = isLoggedIn && userRole === 'Admin';

  const category = useMemo(
    () => (categories || []).find((c) => c.categoryId === categoryId),
    [categories, categoryId]
  );

  const subCategories = category?.subCategories || [];

  useEffect(() => {
    if (isSeller) {
      navigate('/', { replace: true });
    }
  }, [isSeller, navigate]);

  useEffect(() => {
    if (categoriesStatus === 'idle') {
      dispatch(fetchCategoriesWithSubCategories());
    }
  }, [categoriesStatus, dispatch]);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(fetchCart());
    }
  }, [isLoggedIn, dispatch]);

  useEffect(() => {
    if (!categoryId || Number.isNaN(categoryId)) return;
    dispatch(
      fetchProducts({
        categoryId,
        subCategoryId: subCategoryId || null,
        tertiaryCategoryId: tertiaryCategoryId || null,
        quaternaryCategoryId: quaternaryCategoryId || null,
      })
    );
  }, [dispatch, categoryId, subCategoryId, tertiaryCategoryId, quaternaryCategoryId]);

  useEffect(() => {
    if (!category) return;
    const direct = category.imageUrl || category.ImageUrl;
    if (direct) {
      setCategoryThumbUrl(getImageUrl(direct));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/Products`, { params: { categoryId } });
        const arr = Array.isArray(res.data) ? res.data : res.data?.$values ?? [];
        const first = arr.find((p) => getProp(p, 'imageUrl'));
        if (!cancelled && first) {
          setCategoryThumbUrl(getImageUrl(getProp(first, 'imageUrl')));
        }
      } catch {
        if (!cancelled) setCategoryThumbUrl(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [category, categoryId]);

  const brandOptions = useMemo(() => {
    const names = new Set();
    (products || []).forEach((p) => {
      const b = getProp(p, 'brandName');
      if (b && String(b).trim()) names.add(String(b).trim());
    });
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [products]);

  const mrpBounds = useMemo(() => {
    const values = (products || [])
      .map((p) => getProductMrp(p))
      .filter((v) => !Number.isNaN(v) && v >= 0);
    if (values.length === 0) return { min: 0, max: 500 };
    const min = Math.floor(Math.min(...values));
    const max = Math.ceil(Math.max(...values));
    return { min, max: Math.max(max, min + 1) };
  }, [products]);

  useEffect(() => {
    setPriceMin(mrpBounds.min);
    setPriceMax(mrpBounds.max);
    setSelectedBrands([]);
  }, [categoryId, subCategoryId, tertiaryCategoryId, quaternaryCategoryId, mrpBounds.min, mrpBounds.max]);

  const filteredProducts = useMemo(() => {
    let list = [...(products || [])];
    if (selectedBrands.length > 0) {
      list = list.filter((p) => selectedBrands.includes(getProp(p, 'brandName')));
    }
    const min = priceMin ?? mrpBounds.min;
    const max = priceMax ?? mrpBounds.max;
    list = list.filter((p) => {
      const mrp = getProductMrp(p);
      return mrp >= min && mrp <= max;
    });
    switch (sortBy) {
      case 'price-asc':
        list.sort((a, b) => getProductMrp(a) - getProductMrp(b));
        break;
      case 'price-desc':
        list.sort((a, b) => getProductMrp(b) - getProductMrp(a));
        break;
      case 'name-asc':
        list.sort((a, b) =>
          String(getProp(a, 'productName') || '').localeCompare(
            String(getProp(b, 'productName') || ''),
            undefined,
            { sensitivity: 'base' }
          )
        );
        break;
      default:
        break;
    }
    return list;
  }, [products, selectedBrands, priceMin, priceMax, mrpBounds.min, mrpBounds.max, sortBy]);

  const getCartQuantity = useCallback(
    (productId) => {
      const item = cartItems.find((i) => i.productId === productId);
      return item ? item.quantity : 0;
    },
    [cartItems]
  );

  const handleAddToCart = (product, e) => {
    if (e) e.stopPropagation();
    const productId = getProp(product, 'productId');
    const productName = getProp(product, 'productName');
    const price = getProp(product, 'price');
    const originalPrice = getProp(product, 'originalPrice') ?? null;
    const imageUrl = getImageUrl(getProp(product, 'imageUrl'));
    if (getListedProductVariants(product).length > 0) {
      navigate(`/product/${productId}`);
      return;
    }
    const available = getListedProductStock(product);
    if (getCartQuantity(productId) >= available) {
      window.alert(available > 0 ? `Only ${available} in stock.` : 'This item is out of stock.');
      return;
    }
    if (isLoggedIn) {
      dispatch(addToCartAsync({ productId, quantity: 1 }));
    } else {
      dispatch(addToCart({ productId, productName, price, originalPrice, imageUrl, stock: available }));
    }
  };

  const handleIncrement = (product, e) => {
    if (e) e.stopPropagation();
    const productId = getProp(product, 'productId');
    if (getListedProductVariants(product).length > 0) {
      navigate(`/product/${productId}`);
      return;
    }
    const qty = getCartQuantity(productId);
    const available = getListedProductStock(product);
    if (qty >= available) {
      window.alert(available > 0 ? `Only ${available} in stock.` : 'This item is out of stock.');
      return;
    }
    if (isLoggedIn) {
      dispatch(updateCartItemAsync({ productId, quantity: qty + 1 }));
    } else {
      dispatch(updateQuantity({ productId, quantity: qty + 1, stock: available }));
    }
  };

  const handleRemoveFromCart = (product, e) => {
    if (e) e.stopPropagation();
    const productId = getProp(product, 'productId');
    if (isLoggedIn) {
      dispatch(removeFromCartAsync(productId));
    } else {
      dispatch(removeFromCart(productId));
    }
  };

  const handleProductClick = (product) => {
    navigate(`/product/${getProp(product, 'productId')}`);
  };

  const toggleBrand = (brand) => {
    setSelectedBrands((prev) =>
      prev.includes(brand) ? prev.filter((b) => b !== brand) : [...prev, brand]
    );
  };

  const toggleSubCategory = (id) => {
    const next = new URLSearchParams(searchParams);
    if (subCategoryId === id) {
      next.delete('subcategory');
    } else {
      next.set('subcategory', String(id));
    }
    setSearchParams(next, { replace: true });
  };

  const clearFilters = () => {
    setSelectedBrands([]);
    setPriceMin(mrpBounds.min);
    setPriceMax(mrpBounds.max);
    const next = new URLSearchParams(searchParams);
    next.delete('subcategory');
    setSearchParams(next, { replace: true });
  };

  const visibleBrands = showAllBrands ? brandOptions : brandOptions.slice(0, FILTER_PREVIEW_COUNT);
  const visibleSubCategories = showAllSubCategories
    ? subCategories
    : subCategories.slice(0, FILTER_PREVIEW_COUNT);

  const categoryName = category?.categoryName || 'Category';

  if (isSeller) return null;

  if (Number.isNaN(categoryId)) {
    return (
      <div className="plp-page">
        <Header />
        <main className="plp-main">
          <p className="plp-status">Invalid category.</p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="plp-page">
      <Header />
      <main className="plp-main">
        <nav className="plp-breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span className="plp-breadcrumb-sep">/</span>
          <span>{categoryName}</span>
        </nav>

        <div className="plp-layout">
          <aside className="plp-sidebar" aria-label="Filters">
            <div className="plp-filters-head">
              <h2>Filters</h2>
              <button type="button" className="plp-clear-btn" onClick={clearFilters}>
                Clear All
              </button>
            </div>

            {brandOptions.length > 0 && (
              <FilterSection title="Brands">
                <div className="plp-check-list">
                  {visibleBrands.map((brand) => (
                    <label key={brand} className="plp-check-label">
                      <input
                        type="checkbox"
                        checked={selectedBrands.includes(brand)}
                        onChange={() => toggleBrand(brand)}
                      />
                      <span>{brand}</span>
                    </label>
                  ))}
                </div>
                {brandOptions.length > FILTER_PREVIEW_COUNT && (
                  <button
                    type="button"
                    className="plp-more-link"
                    onClick={() => setShowAllBrands((v) => !v)}
                  >
                    {showAllBrands ? 'Show less' : `+ ${brandOptions.length - FILTER_PREVIEW_COUNT} More`}
                  </button>
                )}
              </FilterSection>
            )}

            {subCategories.length > 0 && (
              <FilterSection title="Category">
                <div className="plp-check-list">
                  {visibleSubCategories.map((sub) => (
                    <label key={sub.subCategoryId} className="plp-check-label">
                      <input
                        type="checkbox"
                        checked={subCategoryId === sub.subCategoryId}
                        onChange={() => toggleSubCategory(sub.subCategoryId)}
                      />
                      <span>{sub.subCategoryName}</span>
                    </label>
                  ))}
                </div>
                {subCategories.length > FILTER_PREVIEW_COUNT && (
                  <button
                    type="button"
                    className="plp-more-link"
                    onClick={() => setShowAllSubCategories((v) => !v)}
                  >
                    {showAllSubCategories
                      ? 'Show less'
                      : `+ ${subCategories.length - FILTER_PREVIEW_COUNT} More`}
                  </button>
                )}
              </FilterSection>
            )}

            <FilterSection title="Price Range By M.R.P.">
              <div className="plp-price-range">
                <input
                  type="range"
                  min={mrpBounds.min}
                  max={mrpBounds.max}
                  value={priceMin ?? mrpBounds.min}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setPriceMin(Math.min(v, priceMax ?? mrpBounds.max));
                  }}
                />
                <input
                  type="range"
                  min={mrpBounds.min}
                  max={mrpBounds.max}
                  value={priceMax ?? mrpBounds.max}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setPriceMax(Math.max(v, priceMin ?? mrpBounds.min));
                  }}
                />
                <div className="plp-price-values">
                  <span>₹{formatPrice(priceMin ?? mrpBounds.min)}</span>
                  <span>₹{formatPrice(priceMax ?? mrpBounds.max)}</span>
                </div>
              </div>
            </FilterSection>
          </aside>

          <div className="plp-content">
            <div className="plp-toolbar">
              <div className="plp-category-head">
                {categoryThumbUrl ? (
                  <img src={categoryThumbUrl} alt="" className="plp-category-thumb" />
                ) : (
                  <div className="plp-category-thumb plp-category-thumb-fallback" aria-hidden="true">
                    {String(categoryName).charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <h1 className="plp-category-title">{categoryName}</h1>
                  <p className="plp-item-count">
                    {filteredProducts.length} Item{filteredProducts.length === 1 ? '' : 's'} found
                  </p>
                </div>
              </div>
              <label className="plp-sort-wrap">
                Sort by:
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {productsStatus === 'loading' && (
              <div className="plp-status">Loading products…</div>
            )}
            {productsStatus === 'failed' && (
              <div className="plp-status plp-status-error">
                Error loading products: {productsError}
              </div>
            )}
            {productsStatus === 'succeeded' && filteredProducts.length === 0 && (
              <div className="plp-status">No products found for this category.</div>
            )}
            {productsStatus === 'succeeded' && filteredProducts.length > 0 && (
              <div className="plp-grid">
                {filteredProducts.map((product) => {
                  const productId = getProp(product, 'productId');
                  const productName = getProp(product, 'productName');
                  const imageUrl = getImageUrl(getProp(product, 'imageUrl'));
                  const displayMrp = getProductMrp(product);
                  const qty = getCartQuantity(productId);

                  return (
                    <article key={productId} className="plp-card">
                      <button
                        type="button"
                        className="plp-card-image-btn"
                        onClick={() => handleProductClick(product)}
                        aria-label={productName}
                      >
                        <img
                          src={imageUrl}
                          alt={productName}
                          className="plp-card-image"
                          onError={(e) => {
                            e.target.src = BROKEN_IMAGE_PLACEHOLDER;
                          }}
                        />
                      </button>
                      <button
                        type="button"
                        className="plp-card-name"
                        onClick={() => handleProductClick(product)}
                      >
                        {productName}
                      </button>
                      <p className="plp-card-mrp">
                        <span className="plp-mrp-label">MRP:</span>
                        <span className="plp-mrp-value"> ₹{formatPrice(displayMrp)}</span>
                      </p>
                      {qty > 0 ? (
                        <div className="quantity-selector plp-qty-selector">
                          <button
                            type="button"
                            className="qty-btn delete-btn"
                            onClick={(e) => handleRemoveFromCart(product, e)}
                            title="Remove from cart"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                          </button>
                          <span className="qty-value">{qty}</span>
                          <button
                            type="button"
                            className="qty-btn add-btn"
                            onClick={(e) => handleIncrement(product, e)}
                            title="Add more"
                          >
                            +
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="plp-add-cart-btn"
                          onClick={(e) => handleAddToCart(product, e)}
                          disabled={isAdmin}
                        >
                          <span>Add to cart</span>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16" aria-hidden="true">
                            <circle cx="9" cy="21" r="1" />
                            <circle cx="20" cy="21" r="1" />
                            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                          </svg>
                        </button>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CategoryProductListPage;
